// Async typeahead for FK fields (vendor / location / etc).
//
// Pass a `searchFn` that resolves to candidate options. The picker debounces
// keystrokes (~250ms), shows the results in a dropdown, and clears once a
// selection is made. The current value is rendered as a removable chip.
//
// Decoupled from the API layer — the search function is whatever the parent
// supplies. That keeps this component reusable across collections.

import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

export interface RelationshipOption {
  id: number;
  label: string;
  /** Optional secondary line under the main label (e.g. business name for vendors). */
  sub?: string | null;
}

interface RelationshipPickerProps {
  label: string;
  value: number | null;
  /** When `value` is set, the picker shows this label as the current selection.
   *  Pass null to fall back to "(id N)" fallback rendering. */
  valueLabel?: string | null;
  valueSub?: string | null;
  onChange: (id: number | null, option?: RelationshipOption) => void;
  searchFn: (q: string, signal?: AbortSignal) => Promise<RelationshipOption[]>;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  emptyLabel?: string;
}

export function RelationshipPicker({
  label,
  value,
  valueLabel,
  valueSub,
  onChange,
  searchFn,
  placeholder = 'Type to search…',
  hint,
  required,
  disabled,
  emptyLabel = 'No matches.',
}: RelationshipPickerProps) {
  const reactId = useId();
  const inputId = `rel-${reactId}`;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  // Light debounce so we don't fire a search on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Click-outside to close the dropdown.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useQuery({
    queryKey: ['rel-picker', label, debounced],
    queryFn: ({ signal }) => searchFn(debounced, signal),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
  });

  const isSelected = value != null;

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      <label htmlFor={inputId} className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
        {required ? <span className="ms-0.5 text-[#9f1239]">*</span> : null}
      </label>

      {isSelected ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-[color:var(--bo-ink)]">
              {valueLabel ?? `(id ${value})`}
            </div>
            {valueSub ? (
              <div className="truncate text-[11px] text-[color:var(--bo-mute)]">{valueSub}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="text-[12px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      ) : (
        <input
          id={inputId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="h-10 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none disabled:cursor-not-allowed disabled:bg-[color:var(--bo-bg)]"
        />
      )}

      {hint ? <span className="text-[11px] text-[color:var(--bo-mute)]">{hint}</span> : null}

      {open && !isSelected ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-[color:var(--bo-line)] bg-white shadow-lg">
          {debounced.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">
              Start typing to search…
            </div>
          ) : results.isLoading ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">Searching…</div>
          ) : results.error ? (
            <div className="px-3 py-2 text-[12px] text-[#9f1239]">
              {(results.error as Error).message}
            </div>
          ) : (results.data ?? []).length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">{emptyLabel}</div>
          ) : (
            <ul className="divide-y divide-[color:var(--bo-line)]">
              {(results.data ?? []).map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.id, opt);
                      setQuery('');
                      setOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-left text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]"
                  >
                    <div className="truncate font-medium">{opt.label}</div>
                    {opt.sub ? (
                      <div className="truncate text-[11px] text-[color:var(--bo-mute)]">
                        {opt.sub}
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
